import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Typography, Tooltip, Tag, Divider, Badge, Space } from 'antd';

import * as ToolRequest from '../../../tools/ToolRequest';

import SpinFull from '../../../components/SpinFull';
import { getAutomationDailyTimeline } from './stationZoneAutomationSchedulePreview';

const DAY_MINUTES = 24 * 60;

const formatSettingValue = (settingConfig, value, settingKey, zoneIos) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  if (settingKey && settingKey.endsWith('IoId') && zoneIos.length > 0) {
    const io = zoneIos.find(ioItem => ioItem.id === value);
    return io ? io.name : String(value);
  }
  if (!settingConfig) {
    return String(value);
  }
  switch (settingConfig.type) {
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'option': {
      const option = settingConfig.options && settingConfig.options.find(o => o.value === value);
      return option ? option.label : value;
    }
    default:
      return String(value);
  }
};

const buildSettingDescriptions = (automationType, automationSettings, zoneAutomationConfigsData, zoneIos) => {
  if (!Array.isArray(zoneAutomationConfigsData) || !automationType) {
    return [];
  }
  const config = zoneAutomationConfigsData.find(c => c.type === automationType);
  const settingConfigs = config && config.settings ? config.settings : [];
  if (settingConfigs.length > 0) {
    return settingConfigs.map(sc => ({
      key: sc.key,
      label: sc.title,
      value: formatSettingValue(sc, automationSettings[sc.key], sc.key, zoneIos),
    }));
  }
  return Object.entries(automationSettings).map(([key, value]) => ({
    key,
    label: key,
    value: formatSettingValue(null, value, key, zoneIos),
  }));
};

async function loadZoneIos(zone) {
  const ios = zone.ios || [];
  if (ios.length > 0) {
    return ios;
  }
  try {
    const iosRes = await ToolRequest.request('GET', '/v1/station-zone-io', { zoneId: zone.id, count: 1000 });
    return iosRes.data || iosRes || [];
  } catch {
    return [];
  }
}

export default function StationDashboardAutomationVisualizationModal(props) {
  const {
    zones,
    visible,
    onCancel,
  } = props;

  const zoneAutomationConfigsData = useSelector(state => state.zoneAutomationConfigs);

  const [loaded, setLoaded] = useState(false);
  const [rows, setRows] = useState([]);
  const [hoveredAutomationId, setHoveredAutomationId] = useState(null);

  useEffect(() => {
    if (!visible) {
      return;
    }
    if (!zones || !zones.length) {
      setRows([]);
      setLoaded(true);
      return;
    }

    let cancelled = false;

    setLoaded(false);

    (async () => {
      const nextRows = [];

      for (const zone of zones) {
        const zoneIos = await loadZoneIos(zone);
        if (cancelled) {
          return;
        }

        const automations = zone.automations || [];
        const settingsResults = await Promise.all(
          automations.map(a =>
            ToolRequest.request('GET', `/v1/station-zone-automation/${a.id}/setting`).catch(() => null),
          ),
        );

        if (cancelled) {
          return;
        }

        automations.forEach((automation, i) => {
          const automationSettings = settingsResults[i] && typeof settingsResults[i] === 'object'
            ? settingsResults[i]
            : {};
          const settingsLoadFailed = settingsResults[i] === null;

          nextRows.push({
            zoneId: zone.id,
            zoneName: zone.name,
            automation,
            automationSettings,
            zoneIos,
            settingsLoadFailed,
          });
        });
      }

      if (!cancelled) {
        setRows(nextRows);
        setLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, zones]);

  const timelineForRow = useCallback((row) => {
    const { automation, automationSettings } = row;
    return getAutomationDailyTimeline({
      type: automation.type,
      settings: automationSettings,
    });
  }, []);

  const tooltipTitle = useCallback((row) => {
    const { automation, automationSettings, zoneIos, settingsLoadFailed } = row;
    const items = buildSettingDescriptions(
      automation.type,
      automationSettings,
      zoneAutomationConfigsData,
      zoneIos,
    );

    // Tooltip inner is dark (antd 4); avoid Descriptions/Typography theme colors (they stay dark-on-light and read as invisible).
    const tip = {
      color: 'rgba(255,255,255,0.95)',
      muted: 'rgba(255,255,255,0.65)',
      danger: '#ffccc7',
    };

    return (
      <div
        style={{
          color: tip.color,
          maxWidth: 380,
          maxHeight: 'min(70vh, 480px)',
          overflowY: 'auto',
          fontSize: 12,
        }}
      >
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontWeight: 600 }}>{automation.name}</span>
          <span style={{ marginLeft: 8, color: tip.muted }}>{automation.type}</span>
        </div>
        <div style={{ marginBottom: 8, color: tip.muted }}>
          Status:{' '}
          <Tag color={automation.isEnabled ? 'success' : 'default'}>
            {automation.isEnabled ? 'ON' : 'OFF'}
          </Tag>
        </div>
        {settingsLoadFailed ? (
          <span style={{ color: tip.danger }}>Settings could not be loaded.</span>
        ) : items.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {items.map(item => (
              <div
                key={item.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 120px) minmax(0, 1fr)',
                  columnGap: 10,
                  alignItems: 'start',
                  lineHeight: 1.35,
                }}
              >
                <span
                  title={typeof item.label === 'string' ? item.label : undefined}
                  style={{
                    color: tip.muted,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.label}
                </span>
                <span
                  style={{
                    color: tip.color,
                    wordBreak: 'break-word',
                    minWidth: 0,
                  }}
                >
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <span style={{ color: tip.muted }}>No settings</span>
        )}
      </div>
    );
  }, [zoneAutomationConfigsData]);

  const hourLabels = useMemo(() => (
    Array.from({ length: 24 }, (_, h) => h)
  ), []);

  return (
    <Modal
      title="Automation schedule (24h)"
      visible={visible}
      onCancel={onCancel}
      width="90%"
      footer={null}
      style={{ maxWidth: 1200 }}
    >
      {loaded ? (
        <>
          <div style={{ display: 'flex', marginLeft: 160, marginBottom: 4 }}>
            {hourLabels.map(h => (
              <div
                key={h}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: 11,
                  color: 'rgba(0,0,0,0.45)',
                }}
              >
                {h}
              </div>
            ))}
          </div>

          {zones.map(zone => {
            const zoneRows = rows.filter(r => r.zoneId === zone.id);
            if (!zoneRows.length) {
              return null;
            }
            return (
              <div key={zone.id} style={{ marginBottom: 16 }}>
                <Divider orientation="left" plain style={{ margin: '8px 0' }}>
                  {zone.name}
                </Divider>
                {zoneRows.map(row => {
                  const { automation } = row;
                  const enabled = automation.isEnabled;
                  const { segments, markersMin } = timelineForRow(row);
                  const isHovered = hoveredAutomationId === automation.id;

                  const rowBg = (() => {
                    if (!isHovered) {
                      return enabled ? 'rgba(82, 196, 26, 0.04)' : 'rgba(0,0,0,0.02)';
                    }
                    return enabled ? 'rgba(82, 196, 26, 0.1)' : 'rgba(0,0,0,0.05)';
                  })();

                  return (
                    <Tooltip
                      key={automation.id}
                      title={tooltipTitle(row)}
                      overlayStyle={{ zIndex: 1100, maxWidth: 400 }}
                      overlayInnerStyle={{ maxWidth: 400 }}
                      mouseEnterDelay={0.2}
                      getPopupContainer={() => document.body}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 6,
                          padding: '6px 8px',
                          borderRadius: 4,
                          borderLeft: `3px solid ${enabled ? '#52c41a' : '#bfbfbf'}`,
                          background: rowBg,
                          cursor: 'default',
                        }}
                        onMouseEnter={() => setHoveredAutomationId(automation.id)}
                        onMouseLeave={() => setHoveredAutomationId(null)}
                      >
                        <div
                          style={{
                            width: 148,
                            flexShrink: 0,
                            paddingRight: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <Badge status={enabled ? 'success' : 'default'} />
                          <div
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: 13,
                              fontWeight: enabled ? 500 : 400,
                              color: enabled ? 'rgba(0,0,0,0.88)' : 'rgba(0,0,0,0.45)',
                            }}
                            title={automation.name}
                          >
                            {automation.name}
                          </div>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            height: 22,
                            background: enabled ? '#f0f0f0' : '#fafafa',
                            borderRadius: 2,
                            position: 'relative',
                            minWidth: 0,
                            border: enabled ? 'none' : '1px dashed #e0e0e0',
                          }}
                        >
                          {segments.map((seg, idx) => (
                            <div
                              key={`s-${idx}`}
                              style={{
                                position: 'absolute',
                                left: `${(seg.startMin / DAY_MINUTES) * 100}%`,
                                width: `${((seg.endMin - seg.startMin) / DAY_MINUTES) * 100}%`,
                                top: 2,
                                bottom: 2,
                                background: '#52c41a',
                                borderRadius: 1,
                                opacity: enabled ? 1 : 0.5,
                              }}
                            />
                          ))}
                          {markersMin.map((m, idx) => (
                            <div
                              key={`m-${idx}`}
                              style={{
                                position: 'absolute',
                                left: `${(m / DAY_MINUTES) * 100}%`,
                                width: 2,
                                marginLeft: -1,
                                top: 1,
                                bottom: 1,
                                background: '#1890ff',
                                borderRadius: 1,
                                opacity: enabled ? 1 : 0.5,
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            );
          })}
        </>
      ) : <SpinFull />}
    </Modal>
  );
}
